import { createFileRoute, Outlet } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { useCurrentUser } from '~/hooks/useCurrentUser';

/**
 * The *All Partners* shell — the coordinator's partner directory and the Add
 * Partner form that writes into it.
 *
 * TOP-LEVEL, NOT A CHILD OF `/follow-ups`. Legacy registers Partners as its own
 * group extension (`themes/wbs/modules/follow-up-group-tabs/index.php` calls
 * `bp_register_group_extension` twice, once per follow-up group type), a
 * SIBLING of the follow-ups tab set rather than a sub-tab of it — and
 * `follow-ups.tsx:35-39` paints a hardcoded `{pending-requests, entries}`
 * `TabsBar` above its `Outlet` unconditionally, so a child there would render a
 * two-tab strip with neither tab active. D24 re-homes group tabs to top-level
 * routes; W4.7's `follow-up-network.tsx` is the shipped precedent.
 *
 * A shell exists here and not on `/follow-up-coordinators` for one reason: this
 * family has TWO children (the directory and the form) and that one has a
 * single child, which puts its gate in the component the way
 * `follow-up-request-board.index.tsx:54` does.
 *
 * THE GATE IS SINGLE-ROLE, AND IT IS THE EXACT INVERSE OF W4.7's. Copying
 * `follow-up-network.tsx:38-49` ships an `ADMIN` arm the server refuses:
 * `admin_bypass` is OFF on all four of this group's surfaces, because both tab
 * classes set `$this->parentCheck = false;` (`follow-up-partners-tab.php:12`,
 * `follow-up-coordinators-tab.php:12`) and neither overrides `$adminCanView`
 * (`WBS_Group_Extension.php:13`, default `false`), so `matchRequirements`
 * (`:150-161`) takes neither admin door. Measured live in one session as uid
 * 1808: `.../partners/` → `Page not found`, `.../partners/all-partners/new/` →
 * `Page not found`, while the SAME session gets 200 on that group's
 * `/follow-up-network/`. Server-side the door is
 * `CurrentFollowupCoordinatorDependency`, which raises `ForbiddenError` for
 * everyone else, administrators included.
 *
 * Defence in depth over the set the endpoints enforce, never the only gate —
 * the list and the create each refuse on their own.
 */
function FollowUpPartnersLayout(): ReactElement | null {
  const { me, isPending } = useCurrentUser();

  // `me.profile_type`, and neither of the two hooks that look right:
  // `useCurrentUser().isStudyHelper` folds coordinator, partner, study helper
  // and admin together (`hooks/useCurrentUser.ts:14-19`), and `useIsCoordinator`
  // reads `params.groupName`, which never resolves on a top-level route
  // (`hooks/useIsCoordinator.ts:33-47`).
  const allowed = me?.profile_type === 'FOLLOWUP_COORDINATOR';

  // `/users/me` is pending on first paint and a bare `!allowed` bounces every
  // caller, the coordinator included.
  if (isPending) return null;
  if (!allowed) return <NotFoundScreen />;

  // No title here: the two children carry different headings (*All Partners*
  // vs *Add New Follow-up Partner*), exactly as legacy renders a
  // `pagetitle-header` block per template.
  return <Outlet />;
}

export const Route = createFileRoute('/_app/follow-up-partners')({
  component: FollowUpPartnersLayout,
});
